import { useMsal } from "@azure/msal-react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { MRT_ColumnFiltersState } from "mantine-react-table";
import { useMemo } from "react";
import { postAPI } from "src/utils/fetch";
import { BACKEND_URL } from "./useMetadata";

export interface AuthSearchFilter {
    key: string;
    value: string | string[];
    [key: string]: unknown;
}

export interface AuthSearchHit<T> {
    _id: string;
    _source: T;
    sort: [string, string];
}

export interface AuthSearchResponse<T> {
    hits: {
        total: { value: number };
        hits: AuthSearchHit<T>[];
    };
}

interface AuthSearchCursor {
    docId: string;
    docTimestamp: string;
}

export interface UseAuthSearchPaginationParams {
    queryKey: unknown[];
    path: string;
    columnFilters?: MRT_ColumnFiltersState;
    globalFilter?: string;
    globalFilterKey: string;
    maxResults?: number;
    enabled?: boolean;
}

export function useAuthSearchPagination<T>({
    queryKey,
    path,
    columnFilters = [],
    globalFilter,
    globalFilterKey,
    maxResults = 25,
    enabled = true,
}: UseAuthSearchPaginationParams) {
    const { instance } = useMsal();

    const filters = useMemo((): AuthSearchFilter[] => {
        const active: AuthSearchFilter[] = columnFilters
            .filter((filter) =>
                Array.isArray(filter.value)
                    ? filter.value.filter(Boolean).length > 0
                    : Boolean(filter.value),
            )
            .map((filter) => ({ key: filter.id, value: filter.value as string | string[] }));

        if (globalFilter) {
            active.push({ key: globalFilterKey, value: globalFilter });
        }

        return active;
    }, [columnFilters, globalFilter, globalFilterKey]);

    return useInfiniteQuery<AuthSearchResponse<T>>({
        // Keyed by the filters actually sent, not the raw table state: MRT's global filter
        // input resets "" to undefined after mounting, which would otherwise change the key
        // and fire the same search twice on load.
        queryKey: [...queryKey, filters],
        queryFn: async ({ pageParam }) => {
            const cursor = pageParam as AuthSearchCursor | undefined;
            const url = new URL(path, BACKEND_URL);
            url.searchParams.set("max_results", `${maxResults}`);
            if (cursor?.docId && cursor?.docTimestamp) {
                url.searchParams.set("search_after_doc_id", cursor.docId);
                url.searchParams.set("search_after_doc_timestamp", cursor.docTimestamp);
            }

            const response = await postAPI({
                url: url.toString(),
                instance,
                payload: filters,
            });
            return response.json();
        },
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => {
            const hits = lastPage?.hits?.hits ?? [];
            if (hits.length < maxResults) return undefined;

            const lastHit = hits.at(-1);
            const [docTimestamp, docId] = lastHit?.sort ?? [];
            if (!docTimestamp || !docId) return undefined;

            return { docTimestamp, docId };
        },
        placeholderData: (previousData) => previousData,
        enabled,
        refetchOnWindowFocus: false,
    });
}

export interface UseAuthSearchAllParams {
    queryKey: unknown[];
    path: string;
    maxResults?: number;
    enabled?: boolean;
    staleTime?: number;
    refetchOnMount?: boolean | "always";
}

// Pages through every result of a search endpoint and returns the flattened list.
// Use for pickers/dropdowns that need the full collection rather than a scrollable page.
export function useAuthSearchAll<T>({
    queryKey,
    path,
    maxResults = 100,
    enabled = true,
    staleTime,
    refetchOnMount,
}: UseAuthSearchAllParams) {
    const { instance } = useMsal();

    return useQuery<T[]>({
        queryKey,
        queryFn: async () => {
            const results: T[] = [];
            let cursor: AuthSearchCursor | undefined;

            for (;;) {
                const url = new URL(path, BACKEND_URL);
                url.searchParams.set("max_results", `${maxResults}`);
                if (cursor?.docId && cursor?.docTimestamp) {
                    url.searchParams.set("search_after_doc_id", cursor.docId);
                    url.searchParams.set("search_after_doc_timestamp", cursor.docTimestamp);
                }

                const response = await postAPI({ url: url.toString(), instance, payload: [] });
                const data: AuthSearchResponse<T> = await response.json();
                const hits = data.hits?.hits ?? [];
                results.push(...hits.map((hit) => hit._source));

                if (hits.length < maxResults) break;

                const [docTimestamp, docId] = hits.at(-1)?.sort ?? [];
                if (!docTimestamp || !docId) break;
                cursor = { docId, docTimestamp };
            }

            return results;
        },
        enabled,
        staleTime,
        refetchOnMount,
        refetchOnWindowFocus: false,
    });
}
